// frontend/client/src/components/AICoach.js
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Brain, TrendingUp, Clock, Palette, MessageCircle, Target,
  Zap, Award, Lightbulb, ArrowRight, RefreshCw, AlertCircle,
  Instagram, BarChart3, ChevronDown, Loader2, Sparkles, CalendarDays, Wand2
} from 'lucide-react';

/* STREAMING_CHUNK: Styling the AI Coach dashboard... */
// FIX: --brand-purple, --brand-purple-light, --brand-blue were never
// defined anywhere in the real design system (index.css / useTheme.js
// only define --brand-primary, --brand-secondary, --brand-accent,
// --brand-border). Every reference to those three fell back to nothing,
// so icon colors, hover borders, and the CTA button gradient were all
// silently invisible/unstyled.
const AC_CSS = `
  .ac-root { display: flex; flex-direction: column; gap: 20px; padding: 24px; font-family: var(--font-body); }
  .ac-header { display: flex; align-items: flex-start; justify-content: space-between; flex-wrap: wrap; gap: 12px; }
  .ac-title { font-family: var(--font-display); font-size: 24px; font-weight: 800; color: var(--text-main); margin: 0 0 4px; letter-spacing: -0.02em; display: flex; align-items: center; gap: 8px; }
  .ac-sub { font-size: 14px; color: var(--text-muted); margin: 0; }
  
  .ac-grid { display: grid; grid-template-columns: repeat(auto-fit, minmax(320px, 1fr)); gap: 20px; align-items: start; }
  
  .ac-card { background: var(--bg-surface); border: 1px solid var(--border-light); border-radius: 16px; padding: 24px; box-shadow: var(--shadow-md); position: relative; overflow: hidden; transition: transform 0.2s, box-shadow 0.2s; }
  .ac-card:hover { transform: translateY(-4px); box-shadow: var(--shadow-lg); border-color: var(--brand-border); }
  
  .ac-card-icon-wrap { width: 48px; height: 48px; border-radius: 12px; display: flex; align-items: center; justify-content: center; font-size: 24px; margin-bottom: 16px; }
  .ac-category { font-size: 11px; font-weight: 700; text-transform: uppercase; letter-spacing: 0.08em; margin-bottom: 8px; display: block; }
  .ac-card-title { font-family: var(--font-display); font-size: 18px; font-weight: 700; color: var(--text-main); margin: 0 0 12px; line-height: 1.3; }
  .ac-card-desc { font-size: 14px; color: var(--text-muted); margin: 0 0 24px; line-height: 1.6; }
  
  .ac-btn { display: inline-flex; align-items: center; gap: 8px; padding: 10px 20px; border-radius: 999px; font-size: 13px; font-weight: 700; cursor: pointer; transition: all 0.2s; text-decoration: none; border: none; }
  .ac-btn-primary { background: linear-gradient(135deg, var(--brand-primary), var(--brand-accent)); color: white; box-shadow: 0 4px 15px rgba(139, 92, 246, 0.25); }
  .ac-btn-primary:hover { opacity: 0.9; transform: scale(1.02); }
`;

/* STREAMING_CHUNK: Initializing AI Coach Insights data... */
export default function AICoach() {
  const [isScanning, setIsScanning] = useState(false);
  const navigate = useNavigate();

  const INSIGHTS = [
    {
      id: 'timing-strategy',
      icon: '🧠',
      iconBg: '#F3E8FF',
      categoryColor: 'var(--brand-primary)',
      category: 'Predictive Strategy',
      title: 'Post at 12 PM Tuesday on LinkedIn',
      description: 'Based on our brand scan, your audience engages 45% higher with tech policy content around lunch. Suggested Hook: "AI is securing the economy..."',
      action: 'Schedule this post',
      actionIcon: CalendarDays,
      suggestedData: { 
        platform: 'linkedin', 
        time: '12:00', 
        content: 'AI is securing the economy. But are we ignoring the cybersecurity policies required to sustain it? 👇\n\n[Your insights here...]\n\n#CyberSecurity #AIPolicy',
        isAiGenerated: true
      }
    },
    {
      id: 'viral-hook',
      icon: '🎣',
      iconBg: '#DBEAFE',
      categoryColor: 'var(--brand-accent)',
      category: 'Content Optimization',
      title: 'Your TikTok hooks are too slow',
      description: 'Your videos are losing 60% of viewers in the first 3 seconds. Try starting with a bold question instead of an introduction.',
      action: 'Rewrite Hooks in Content Lab',
      actionIcon: Wand2,
      navTarget: '/dashboard/content-lab'
    },
    {
      id: 'campaign-grouping',
      icon: '🎯',
      iconBg: '#DCFCE7',
      categoryColor: '#D97706',
      category: 'Campaign Insight',
      title: 'Bundle your Cybersecurity posts',
      description: 'You have 3 isolated posts about data privacy performing well. Grouping these into a named Campaign will let you track their combined ROI.',
      action: 'Create Campaign',
      actionIcon: Target,
      navTarget: '/dashboard/campaigns'
    }
  ];

  /* STREAMING_CHUNK: Handling user actions and routing... */
  const handleAction = (insight) => {
    if (insight.suggestedData) {
      navigate('/dashboard/calendar', { state: { aiSuggestion: insight.suggestedData } });
    } else if (insight.navTarget) {
      navigate(insight.navTarget);
    }
  };

  /* STREAMING_CHUNK: Rendering the AI Coach UI... */
  return (
    <div className="ac-root">
      <style>{AC_CSS}</style>
      
      <div className="ac-header">
        <div>
          <h1 className="ac-title"><Brain color="var(--brand-primary)" /> Infleuince AI Coach</h1>
          <p className="ac-sub">Your predictive operating system. Analyzing your performance to suggest your next move.</p>
        </div>
        <button 
          className="ac-btn" 
          style={{ background: 'var(--bg-surface)', border: '1px solid var(--border-light)', color: 'var(--text-main)' }}
          onClick={() => { setIsScanning(true); setTimeout(() => setIsScanning(false), 2000); }}
        >
          {isScanning ? <Loader2 size={14} className="vt-spin" /> : <RefreshCw size={14} />} 
          Run Fresh Brand Scan
        </button>
      </div>

      <div className="ac-grid">
        {INSIGHTS.map((insight) => (
          <div key={insight.id} className="ac-card">
            <div className="ac-card-icon-wrap" style={{ background: insight.iconBg }}>
              {insight.icon}
            </div>
            <span className="ac-category" style={{ color: insight.categoryColor }}>
              {insight.category}
            </span>
            <h3 className="ac-card-title">{insight.title}</h3>
            <p className="ac-card-desc">{insight.description}</p>
            
            <button className="ac-btn ac-btn-primary" onClick={() => handleAction(insight)}>
              <insight.actionIcon size={16} />
              {insight.action}
            </button>
          </div>
        ))}
      </div>
    </div>
  );
}